import { NextComponentType } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { IRouterQuery } from 'src/interfaces/routes';

import { Banner, BANNER_TYPES } from '@sravni/react-advertisement';
import { PreFooter } from '@sravni/react-pre-footer';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';

import { useBanners } from '@store/hooks/advertising';
import { useCurrentLocation } from '@store/hooks/locations';
import { setFilters } from '@store/reducers/filters';
import { fetchMetadata } from '@store/reducers/metadata';
import { setProductOptions, setVisibleActiveProductCategory } from '@store/reducers/productTags';
import { setRating } from '@store/reducers/ratings';
import { getLocationSeoParams } from '@utils/metadata';

import { RatingsContent } from '../../components/RatingsContent';
import { useMetadataByPath } from '../../hooks/useMetadataByPath';
import { App } from '../../types';
import { isServer } from '../../utils/isServer';
import { AB_NAME } from '../_app';

const ListPage: NextComponentType<App.ReduxNextPageContext<IRouterQuery>> = () => {
  const banners = useBanners();
  const dispatch = useDispatch();
  const router = useRouter();
  const metadata = useMetadataByPath();
  const location = useCurrentLocation();

  useEffect(() => {
    dispatch(
      fetchMetadata({
        url: router.asPath.split('?')?.[0],
        location: getLocationSeoParams(location),
      }),
    );
  }, [router.asPath, location, dispatch]);

  return (
    <Interceptor componentName="rating-list-component">
      <RatingsContent />
      {!!metadata?.prefooter && (
        <div className={'h-mt-40'}>
          <PreFooter columns={metadata?.prefooter} />
        </div>
      )}
      <Banner banner={banners?.[BANNER_TYPES.OUT_OF_PAGE]} type={BANNER_TYPES.OUT_OF_PAGE} abName={AB_NAME} />
    </Interceptor>
  );
};

ListPage.getInitialProps = (context: App.ReduxNextPageContext<IRouterQuery>) => {
  const { store } = context;

  const { dispatch } = store;
  if (isServer) {
    if (context.req.__FILTERS__) {
      dispatch(setFilters(context.req.__FILTERS__));
      dispatch(setVisibleActiveProductCategory(context.req.__FILTERS__.productCategoryByPerson));
    }

    if (context.req.__TAGS_WITH_PERSON__) {
      context.req.__TAGS_WITH_PERSON__.forEach(([productCategoryByPerson, { tags, alias }]) => {
        dispatch(setProductOptions({ tagsOptions: tags, alias, productCategoryByPerson }));
      });
    }

    if (context.req.__RATING__) {
      dispatch(setRating(context.req.__RATING__));
    }
  }

  return {};
};

export default ListPage;
